/**
 * Authentication context and hooks for Studio Bereikbaar Platform V4
 * Keycloak OIDC authentication with session-based auth (Keycloak federates
 * to Microsoft Entra ID; users still sign in with their Microsoft account)
 */
import React, { createContext, useContext, useEffect, useState } from 'react'
import { authAPI, MicrosoftUser } from './api'

interface AuthContextType {
  user: MicrosoftUser | null
  isLoading: boolean
  initiateLogin: () => void
  logout: () => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<MicrosoftUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check if user is authenticated on mount
  useEffect(() => {
    let mounted = true // Track if component is still mounted

    const checkAuth = async () => {
      // DEV MODE: Skip authentication if flag is set
      if (import.meta.env.VITE_SKIP_AUTH === 'true') {
        const mockUser: MicrosoftUser = {
          id: 'dev-user',
          email: 'dev@studiobereikbaar.nl',
          name: 'Development User'
        }
        if (mounted) {
          setUser(mockUser)
          setIsLoading(false)
        }
        return
      }

      try {
        // Try to get user from session cookie
        const userData = await authAPI.checkAuth()
        if (!mounted) return // Don't update if unmounted

        if (userData) {
          setUser(userData)
          localStorage.setItem('sb_user', JSON.stringify(userData))
        } else {
          // No valid session, clear any stored user data
          localStorage.removeItem('sb_user')
          setUser(null)
        }
      } catch (error) {
        if (!mounted) return // Don't update if unmounted

        console.warn('Auth check failed:', error)
        localStorage.removeItem('sb_user')
        setUser(null)
      } finally {
        if (mounted) {
          // Small delay to ensure backend session is fully established
          // This prevents race conditions after OAuth callback
          await new Promise(resolve => setTimeout(resolve, 100))
          setIsLoading(false)
        }
      }
    }

    checkAuth()

    // Cleanup function
    return () => {
      mounted = false
    }
  }, [])

  const initiateLogin = () => {
    // Redirect to Microsoft authentication
    authAPI.initiateLogin()
  }

  const logout = async () => {
    setIsLoading(true)
    try {
      await authAPI.logout()
      setUser(null)
      localStorage.removeItem('sb_user')
    } catch (error) {
      console.warn('Logout failed:', error)
      // Still clear local state
      setUser(null)
      localStorage.removeItem('sb_user')
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    initiateLogin,
    logout,
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Helper hook for protected routes
export const useRequireAuth = () => {
  const { user, isLoading } = useAuth()

  useEffect(() => {
    if (!isLoading && !user) {
      window.location.href = '/login'
    }
  }, [user, isLoading])

  return { user, isLoading }
}